import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Wrench, Shield, MapPin, Clock } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { fadeUp, staggerContainer } from "@/utils/animations";
import HeroSection from "@/components/home/HeroSection";
import TestimonialsSection from "@/components/home/TestimonialsSection";

const VIEWPORT_ONCE = { once: true, margin: "-100px" };

const faqs = [
  { q: "What materials do you use for your PVC products?", a: "We use commercial-grade, heavy-duty PVC materials sourced from leading Australian and international suppliers. Our PVC is UV-stabilized, flame-retardant, and designed to withstand harsh industrial environments." },
  { q: "How long does installation typically take?", a: "Installation timeframes vary by project size. Small to medium installations are typically completed within 1-3 days. Large-scale projects may take 5-10 business days. We work around your schedule to minimize downtime." },
  { q: "Do you offer custom sizes and configurations?", a: "Absolutely. Every product we manufacture is custom-built to your exact specifications. We conduct on-site measurements and consultations to ensure a perfect fit for your facility." },
  { q: "Are your products compliant with Australian safety standards?", a: "Yes, all our products comply with relevant Australian Standards including fire resistance, UV protection, and workplace safety requirements. We can provide compliance certificates upon request." },
  { q: "What warranty do you offer?", a: "We offer comprehensive warranties on all our products, typically ranging from 5-10 years depending on the product type. Our PVC materials are designed for long-term durability in demanding conditions." },
];

const HERO_IMG = "https://static.prod-images.emergentagent.com/jobs/f6a3f174-a193-4e97-9f7a-54fb8e64a616/images/93ce25f58da721f380c5745ae16b786d932b3e82d9e970d243d8da5bfe87cbb2.png";

const services = [
  { icon: Wrench, title: "Custom Fabrication", desc: "Bespoke industrial textile solutions tailored to your exact specifications and operational requirements." },
  { icon: Shield, title: "Heavy-Duty PVC Systems", desc: "Commercial-grade PVC walls and curtains built to withstand the harshest industrial environments." },
  { icon: MapPin, title: "Australian Made", desc: "Proudly manufactured in Western Australia with locally sourced materials and expert craftsmanship." },
  { icon: Clock, title: "Fast Turnaround", desc: "Rapid manufacturing and installation to minimize your operational downtime." },
];

const products = [
  { name: "PVC Dividing Walls", img: "https://customer-assets.emergentagent.com/job_industrial-textiles-1/artifacts/ewmocaic_IMG_4691.JPEG", desc: "Durable warehouse partition systems" },
  { name: "Sliding Curtains", img: "https://static.prod-images.emergentagent.com/jobs/f6a3f174-a193-4e97-9f7a-54fb8e64a616/images/a8cc9264b68745bb769ea851b3303608393efc5707361b86599777931788f6aa.png", desc: "Flexible industrial sliding solutions" },
  { name: "Welding Screens", img: "https://static.prod-images.emergentagent.com/jobs/f6a3f174-a193-4e97-9f7a-54fb8e64a616/images/86d0c5b450f80735c70910374ac2d2b270c24c5559a77b8c219deef71f9de151.png", desc: "UV-protective safety screens" },
  { name: "Industrial Covers", img: "https://static.prod-images.emergentagent.com/jobs/f6a3f174-a193-4e97-9f7a-54fb8e64a616/images/c6219fe4f1590063ac540153a1f2177151af5acdf85baf614f322b307bf27f05.png", desc: "Heavy-duty protective covers" },
];

const industries = [
  { name: "Warehousing", img: "https://images.unsplash.com/photo-1772298783095-be38fa901232?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MTJ8MHwxfHNlYXJjaHwyfHxpbmR1c3RyaWFsJTIwd2FyZWhvdXNlJTIwY3VydGFpbiUyMGRpdmlkZXJ8ZW58MHx8fHwxNzc5Njc3NDI0fDA&ixlib=rb-4.1.0&q=85" },
  { name: "Mining", img: "https://images.unsplash.com/photo-1587919968590-fbc98cea6c9a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODR8MHwxfHNlYXJjaHwzfHxtaW5pbmclMjBvcGVyYXRpb25zfGVufDB8fHx8MTc3OTY3NzI4OHww&ixlib=rb-4.1.0&q=85" },
  { name: "Manufacturing", img: "https://images.unsplash.com/photo-1717386255773-1e3037c81788?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzV8MHwxfHNlYXJjaHwxfHxtYW51ZmFjdHVyaW5nJTIwZmFjdG9yeSUyMGZsb29yfGVufDB8fHx8MTc3OTY3NzQyNHww&ixlib=rb-4.1.0&q=85" },
  { name: "Logistics", img: "https://images.unsplash.com/photo-1645736315000-6f788915923b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjV8MHwxfHNlYXJjaHwyfHxsb2dpc3RpY3MlMjB3YXJlaG91c2UlMjBmb3JrbGlmdHxlbnwwfHx8fDE3Nzk2Nzc0MjR8MA&ixlib=rb-4.1.0&q=85" },
  { name: "Construction", img: "https://images.unsplash.com/photo-1599707254554-027aeb4deacd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHwxfHxjb25zdHJ1Y3Rpb24lMjBzaXRlJTIwY29tbWVyY2lhbCUyMGJ1aWxkaW5nfGVufDB8fHx8MTc3OTY3NzQyNHww&ixlib=rb-4.1.0&q=85" },
  { name: "Agriculture", img: "https://images.unsplash.com/photo-1638215640640-c51055587778?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHwxfHxhZ3JpY3VsdHVyZSUyMGZhcm0lMjBzaGVkfGVufDB8fHx8MTc3OTY3NzQyNHww&ixlib=rb-4.1.0&q=85" },
];

export default function HomePage() {
  return (
    <div data-testid="home-page">
      <HeroSection />

      {/* Services */}
      <section data-testid="services-section" className="py-24 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={VIEWPORT_ONCE}
            variants={staggerContainer}
          >
            <motion.div variants={fadeUp} className="text-center mb-16">
              <div className="industrial-line mx-auto mb-6" />
              <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4" style={{ fontFamily: "'Clash Display', sans-serif" }}>
                Why Choose NG Industries
              </h2>
              <p className="text-base text-[#A3A3A3] max-w-2xl mx-auto">
                Decades of expertise in industrial textile manufacturing, delivering reliable solutions for Australia's most demanding industries.
              </p>
            </motion.div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {services.map((s, i) => (
                <motion.div
                  key={s.title}
                  variants={fadeUp}
                  custom={i}
                  className="bg-[#141414] border border-[#2E2E2E] p-8 hover:border-[#FF5722] hover:-translate-y-1 transition-all duration-300 group"
                >
                  <div className="w-12 h-12 bg-[#FF5722]/10 flex items-center justify-center mb-6 group-hover:bg-[#FF5722]/20 transition-colors">
                    <s.icon size={24} className="text-[#FF5722]" />
                  </div>
                  <h3 className="text-lg font-bold mb-3" style={{ fontFamily: "'Clash Display', sans-serif" }}>{s.title}</h3>
                  <p className="text-sm text-[#A3A3A3] leading-relaxed">{s.desc}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Featured Products */}
      <section data-testid="featured-products-section" className="py-24 bg-[#141414]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={VIEWPORT_ONCE}
            variants={staggerContainer}
          >
            <motion.div variants={fadeUp} className="flex flex-col sm:flex-row justify-between items-start sm:items-end gap-4 mb-12">
              <div>
                <div className="industrial-line mb-6" />
                <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4" style={{ fontFamily: "'Clash Display', sans-serif" }}>
                  Featured Products
                </h2>
                <p className="text-base text-[#A3A3A3] max-w-lg">
                  Engineered for durability. Built for performance.
                </p>
              </div>
              <Link
                to="/products"
                data-testid="view-all-products-btn"
                className="text-[#FF5722] text-sm font-semibold flex items-center gap-1 hover:gap-2 transition-all"
              >
                View All Products <ArrowRight size={14} />
              </Link>
            </motion.div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {products.map((p, i) => (
                <motion.div
                  key={p.name}
                  variants={fadeUp}
                  custom={i}
                  className="group bg-[#0A0A0A] border border-[#2E2E2E] overflow-hidden hover:border-[#FF5722] transition-all duration-300"
                >
                  <div className="aspect-[4/3] overflow-hidden">
                    <img
                      src={p.img}
                      alt={p.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-6">
                    <h3 className="font-bold mb-2" style={{ fontFamily: "'Clash Display', sans-serif" }}>{p.name}</h3>
                    <p className="text-sm text-[#A3A3A3] mb-4">{p.desc}</p>
                    <Link
                      to="/products"
                      className="text-[#FF5722] text-sm font-semibold flex items-center gap-1 hover:gap-2 transition-all"
                    >
                      Learn More <ArrowRight size={14} />
                    </Link>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Industries */}
      <section data-testid="industries-section" className="py-24 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={VIEWPORT_ONCE}
            variants={staggerContainer}
          >
            <motion.div variants={fadeUp} className="text-center mb-16">
              <div className="industrial-line mx-auto mb-6" />
              <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4" style={{ fontFamily: "'Clash Display', sans-serif" }}>
                Industries We Serve
              </h2>
              <p className="text-base text-[#A3A3A3] max-w-2xl mx-auto">
                Trusted by leading businesses across Australia's core industrial sectors.
              </p>
            </motion.div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {industries.map((ind, i) => (
                <motion.div
                  key={ind.name}
                  variants={fadeUp}
                  custom={i}
                  className="relative group overflow-hidden aspect-square cursor-pointer"
                >
                  <img src={ind.img} alt={ind.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                  <div className="absolute inset-0 bg-black/50 group-hover:bg-black/30 transition-colors duration-300" />
                  <div className="absolute inset-0 flex items-end p-4">
                    <span className="text-white font-bold text-sm" style={{ fontFamily: "'Clash Display', sans-serif" }}>{ind.name}</span>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      <TestimonialsSection />

      {/* FAQ */}
      <section data-testid="faq-section" className="py-24 bg-[#0A0A0A]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="industrial-line mx-auto mb-6" />
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4" style={{ fontFamily: "'Clash Display', sans-serif" }}>
              Frequently Asked Questions
            </h2>
          </div>
          <Accordion type="single" collapsible className="space-y-3">
            {faqs.map((faq) => (
              <AccordionItem
                key={faq.q}
                value={faq.q}
                className="bg-[#141414] border border-[#2E2E2E] px-6 data-[state=open]:border-[#FF5722]"
              >
                <AccordionTrigger
                  data-testid={`faq-trigger-${faq.q.slice(0, 20).replace(/\s/g, '-').toLowerCase()}`}
                  className="text-sm sm:text-base font-semibold text-white hover:text-[#FF5722] py-5 [&[data-state=open]]:text-[#FF5722]"
                  style={{ fontFamily: "'Clash Display', sans-serif" }}
                >
                  {faq.q}
                </AccordionTrigger>
                <AccordionContent className="text-sm text-[#A3A3A3] leading-relaxed pb-5">
                  {faq.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA Banner */}
      <section data-testid="cta-section" className="py-24 bg-[#FF5722]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-white mb-6" style={{ fontFamily: "'Clash Display', sans-serif" }}>
            Ready to Transform Your Workspace?
          </h2>
          <p className="text-base text-white/80 mb-10 max-w-2xl mx-auto">
            Get a custom quote for your industrial textile solution. Our team is ready to engineer the perfect solution for your facility.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/quote"
              data-testid="cta-quote-btn"
              className="bg-white text-[#FF5722] px-8 py-3.5 font-semibold text-sm hover:bg-gray-100 transition-colors"
            >
              Request a Quote
            </Link>
            <Link
              to="/contact"
              data-testid="cta-contact-btn"
              className="border-2 border-white text-white px-8 py-3.5 font-semibold text-sm hover:bg-white hover:text-[#FF5722] transition-all"
            >
              Contact Us
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
